import React, { useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import {
  CreditCardIcon,
  PlusIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import SubscriptionPlans from '../../components/subscriptions/SubscriptionPlans';
import SubscriptionForm from '../../components/subscriptions/SubscriptionForm';
import ActiveSubscriptions from '../../components/subscriptions/ActiveSubscriptions';
import { mockSubscriptions, mockSubscriptionPlans } from '../../utils/mockData';

const SubscriptionsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch subscriptions data
  const { data: subscriptionsData } = useQuery('subscriptions', async () => {
    try {
      const response = await axios.get('/subscriptions');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API abonnements)");
      // Utiliser les données fictives en cas d'erreur
      return { subscriptions: mockSubscriptions };
    }
  });

  // Fetch subscription plans
  const { data: plansData } = useQuery('subscription-plans', async () => {
    try {
      const response = await axios.get('/subscriptions/plans');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API plans)");
      // Utiliser les données fictives en cas d'erreur
      return { plans: mockSubscriptionPlans };
    }
  });

  // Normalisation des abonnements pour que le composant UI dispose des mêmes clés quel que soit le backend ou les données fictives
  const rawSubscriptions = subscriptionsData?.subscriptions || mockSubscriptions;

  const subscriptions = rawSubscriptions.map((sub) => {
    // Member name
    let memberName = sub.memberName;
    if (!memberName && sub.member) {
      memberName = `${sub.member.firstName || ''} ${sub.member.lastName || ''}`.trim();
    }

    // Plan name
    const planName = sub.planName || (sub.plan && sub.plan.name) || '';

    // Amount (price)
    const amount = sub.amount ?? sub.price ?? 0;

    // Status
    let status = sub.status;
    if (!status) {
      if (sub.isActive === false) status = 'suspended';
      else {
        const now = new Date();
        status = new Date(sub.endDate) <= now ? 'expired' : 'active';
      }
    }

    return {
      ...sub,
      memberName,
      planName,
      amount,
      status,
    };
  });
  // Get data from query results or fall back to mock data
  const plans = plansData?.plans || mockSubscriptionPlans;

  // Calculate active subscriptions
  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
  
  // Calculate expiring subscriptions (within next 7 days)
  const now = new Date();
  const next7Days = new Date();
  next7Days.setDate(now.getDate() + 7);
  
  const expiringSubscriptions = activeSubscriptions.filter(sub => {
    const endDate = new Date(sub.endDate);
    return endDate > now && endDate <= next7Days;
  });

  // Calculate monthly revenue
  const totalRevenue = activeSubscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0);

  // Calculer les statistiques
  const stats = {
    totalSubscriptions: subscriptions.length,
    activeSubscriptions: activeSubscriptions.length,
    expiringThisWeek: expiringSubscriptions.length,
    monthlyRevenue: totalRevenue,
  };

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: CreditCardIcon },
    { id: 'active', name: 'Abonnements Actifs', icon: CheckCircleIcon },
    { id: 'plans', name: 'Plans & Tarifs', icon: CurrencyDollarIcon },
    { id: 'expiring', name: 'Expirations', icon: ExclamationTriangleIcon },
  ];

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('subscriptions')}
              </h1>
              <p className="text-white/60 mt-2">
                Gestion complète des abonnements et forfaits
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/subscriptions/plans/new"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nouveau Plan</span>
              </Link>
              <Link
                to="/subscriptions/new"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nouvel Abonnement</span>
              </Link>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Total Abonnements</p>
                  <p className="text-3xl font-bold text-white mt-2">{stats.totalSubscriptions}</p>
                </div>
                <div className="p-3 bg-blue-500/20 text-blue-400 rounded-2xl">
                  <CreditCardIcon className="h-8 w-8" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Abonnements Actifs</p>
                  <p className="text-3xl font-bold text-white mt-2">{stats.activeSubscriptions}</p>
                </div>
                <div className="p-3 bg-green-500/20 text-green-400 rounded-2xl">
                  <CheckCircleIcon className="h-8 w-8" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Expirent bientôt</p>
                  <p className="text-3xl font-bold text-white mt-2">{stats.expiringThisWeek}</p>
                </div>
                <div className="p-3 bg-yellow-500/20 text-yellow-400 rounded-2xl">
                  <ExclamationTriangleIcon className="h-8 w-8" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Revenus Mensuels</p>
                  <p className="text-3xl font-bold text-white mt-2">{stats.monthlyRevenue.toLocaleString()} DA</p>
                </div>
                <div className="p-3 bg-purple-500/20 text-purple-400 rounded-2xl">
                  <CurrencyDollarIcon className="h-8 w-8" />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {/* Recent Subscriptions */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Abonnements Récents
                  </h3>
                  {subscriptions.length > 0 ? (
                    <div className="space-y-3">
                      {subscriptions.slice(0, 5).map((subscription) => (
                        <div
                          key={subscription.id}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
                        >
                          <div>
                            <p className="text-white font-medium">{subscription.memberName}</p>
                            <p className="text-white/60 text-sm">{subscription.planName}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <p className="text-white text-sm">{subscription.amount?.toLocaleString()} DA</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              subscription.status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : subscription.status === 'expired'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {subscription.status === 'active' ? 'Actif' : 
                               subscription.status === 'expired' ? 'Expiré' : 'Suspendu'}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="pt-3 border-t border-white/10">
                        <Link to="/subscriptions/list" className="text-blue-400 text-sm hover:underline">
                          Voir tous les abonnements ({subscriptions.length})
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCardIcon className="h-12 w-12 text-white/30 mx-auto mb-4" />
                      <p className="text-white/60">Aucun abonnement trouvé</p>
                      <Link 
                        to="/subscriptions/new" 
                        className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
                      >
                        Créer votre premier abonnement
                      </Link>
                    </div>
                  )}
                </div>

                {/* Popular Plans */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Plans Populaires
                  </h3>
                  {plans.length > 0 ? (
                    <div className="space-y-3">
                      {plans.slice(0, 4).map((plan) => (
                        <div
                          key={plan.id}
                          className={`flex items-center justify-between p-3 bg-white/5 rounded-xl ${
                            plan.isPopular ? 'border border-purple-500/30' : ''
                          }`}
                        >
                          <div>
                            <div className="flex items-center">
                              <p className="text-white font-medium">{plan.name}</p>
                              {plan.isPopular && (
                                <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                                  Populaire
                                </span>
                              )}
                            </div>
                            <p className="text-white/60 text-sm">{plan.duration}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{plan.price.toLocaleString()} DA</p>
                            <p className="text-white/60 text-xs">
                              {activeSubscriptions.filter(sub => sub.planId === plan.id).length || 0} abonnés
                            </p>
                          </div>
                        </div>
                      ))}
                      <div className="pt-3 border-t border-white/10">
                        <Link to="/subscriptions/plans" className="text-blue-400 text-sm hover:underline">
                          Gérer les plans ({plans.length})
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CurrencyDollarIcon className="h-12 w-12 text-white/30 mx-auto mb-4" />
                      <p className="text-white/60">Aucun plan configuré</p>
                      <Link 
                        to="/subscriptions/plans/new" 
                        className="mt-4 inline-block bg-purple-600 text-white px-4 py-2 rounded-lg text-sm"
                      >
                        Créer un plan
                      </Link>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'active' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <ActiveSubscriptions subscriptions={activeSubscriptions} />
              </motion.div>
            )}

            {activeTab === 'plans' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <SubscriptionPlans plans={plans} />
              </motion.div>
            )}

            {activeTab === 'expiring' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <ActiveSubscriptions subscriptions={expiringSubscriptions} />
              </motion.div>
            )}
          </div>
        </div>
      } />
      
      <Route path="/new" element={<SubscriptionForm />} />
      <Route path="/:id/edit" element={<SubscriptionForm />} />
      <Route path="/plans/new" element={<SubscriptionForm type="plan" />} />
      <Route path="/plans/:id/edit" element={<SubscriptionForm type="plan" />} />
      <Route path="/list" element={<ActiveSubscriptions subscriptions={subscriptions} />} />
      <Route path="/plans" element={<SubscriptionPlans plans={plans} />} />
    </Routes>
  );
};

export default SubscriptionsPage; 